/**
 * The two halves of one accessibility contract: the message a field renders
 * when its value is refused, and the helper that points the field at it.
 *
 * They live in one file because a caller that renders one almost always needs
 * the other. A `FieldError` whose `id` nothing references is invisible to a
 * screen-reader user until the message happens to be read, and an
 * `aria-describedby` naming an id that was never rendered is a dangling
 * reference. Keeping the pair together is what makes that mistake hard to make.
 *
 * Five byte-identical copies of this component existed before #527 — in
 * `NewProjectDialog`, `NewAssetDialog`, `NewAlertMatrixDialog`,
 * `PreviewTargetControl`, and the questionnaire create dialog #638 removed
 * with the family's authoring surface. Duplicating markup that
 * carries `role` and `id` is a correctness risk rather than an untidiness: a
 * correction applied to four of five copies looks complete in review and is not.
 */

/**
 * The message a field renders when its own value is refused.
 *
 * `role="alert"` is deliberately not configurable. A refused value has taken
 * something away from the person who just typed it, and they are looking at the
 * field when it happens, so interrupting is the correct behaviour rather than a
 * choice each caller re-makes.
 *
 * The `id` is load-bearing: pass the same value to `joinDescriptionIds` for the
 * control this message belongs to.
 */
export function FieldError({ id, message }: { id: string; message: string }) {
  return (
    <p id={id} role="alert" className="text-xs text-destructive">
      {message}
    </p>
  );
}

/**
 * Builds the `aria-describedby` value for a control described by more than one
 * node — typically a `Field`'s own hint plus the id of a `FieldError` that may
 * or may not be rendered this pass.
 *
 * Returns `undefined` rather than `""` when nothing describes the control, so
 * React omits the attribute entirely. The distinction matters: an empty
 * `aria-describedby` is a reference to nothing, not the absence of a reference,
 * and assistive technology is entitled to report it as broken.
 */
export function joinDescriptionIds(
  ...ids: Array<string | undefined>
): string | undefined {
  const joined = ids.filter((id): id is string => id !== undefined).join(" ");
  return joined === "" ? undefined : joined;
}
